import React, { useEffect, useCallback } from 'react';

const CORES = [
  { label: 'Vermelho', value: '#e53935' },
  { label: 'Verde', value: '#43a047' },
  { label: 'Azul', value: '#1e88e5' },
  { label: 'Amarelo', value: '#fdd835' },
];

const FERRAMENTAS = [
  { id: 'retangulo', label: '▭', title: 'Retângulo' },
  { id: 'circulo', label: '○', title: 'Círculo' },
  { id: 'seta', label: '➜', title: 'Seta' },
  { id: 'texto', label: 'T', title: 'Texto' },
];

function redraw(canvas, img, shapes, currentShape = null) {
  if (!canvas || !img) return;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  [...shapes, ...(currentShape ? [currentShape] : [])].forEach(s => drawShape(ctx, s));
}

function drawShape(ctx, s) {
  ctx.strokeStyle = s.cor;
  ctx.fillStyle = s.cor;
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  if (s.tipo === 'retangulo') {
    ctx.beginPath();
    ctx.strokeRect(s.x, s.y, s.w, s.h);
  } else if (s.tipo === 'circulo') {
    const rx = Math.abs(s.w) / 2, ry = Math.abs(s.h) / 2;
    const cx = s.x + s.w / 2, cy = s.y + s.h / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
    ctx.stroke();
  } else if (s.tipo === 'seta') {
    const ex = s.x + s.w, ey = s.y + s.h;
    const angle = Math.atan2(s.h, s.w);
    const len = 18;
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ex, ey);
    ctx.lineTo(ex - len * Math.cos(angle - 0.4), ey - len * Math.sin(angle - 0.4));
    ctx.moveTo(ex, ey);
    ctx.lineTo(ex - len * Math.cos(angle + 0.4), ey - len * Math.sin(angle + 0.4));
    ctx.stroke();
  } else if (s.tipo === 'texto') {
    ctx.font = `bold ${s.size || 20}px Arial`;
    ctx.fillText(s.texto, s.x, s.y);
  }
}

function getPos(canvas, e) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;
  return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
}

export default function EditorImagem({
  url, ferramenta, setFerramenta, cor, setCor,
  textoInput, setTextoInput, textoPos, setTextoPos,
  canvasRef, drawingRef, startRef, shapesRef, imgRef,
  onCancelar, onEnviar
}) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !url) return;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const maxW = Math.min(window.innerWidth * 0.85, 900);
      const maxH = window.innerHeight * 0.65;
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > maxW) { h = h * maxW / w; w = maxW; }
      if (h > maxH) { w = w * maxH / h; h = maxH; }
      canvas.width = w;
      canvas.height = h;
      redraw(canvas, img, shapesRef.current);
    };
    img.src = url;
  }, [url]);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pos = getPos(canvas, e);
    if (ferramenta === 'texto') { setTextoPos(pos); return; }
    drawingRef.current = true;
    startRef.current = pos;
  }, [ferramenta]);

  const onMouseMove = useCallback((e) => {
    if (!drawingRef.current || ferramenta === 'texto') return;
    const canvas = canvasRef.current;
    const pos = getPos(canvas, e);
    const s = startRef.current;
    redraw(canvas, imgRef.current, shapesRef.current,
      { tipo: ferramenta, cor, x: s.x, y: s.y, w: pos.x - s.x, h: pos.y - s.y });
  }, [ferramenta, cor]);

  const onMouseUp = useCallback((e) => {
    if (!drawingRef.current || ferramenta === 'texto') return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    const pos = getPos(canvas, e);
    const s = startRef.current;
    if (Math.abs(pos.x - s.x) < 3 && Math.abs(pos.y - s.y) < 3) return;
    shapesRef.current = [...shapesRef.current,
      { tipo: ferramenta, cor, x: s.x, y: s.y, w: pos.x - s.x, h: pos.y - s.y }];
    redraw(canvas, imgRef.current, shapesRef.current);
  }, [ferramenta, cor]);

  const adicionarTexto = () => {
    if (!textoInput.trim() || !textoPos) return;
    shapesRef.current = [...shapesRef.current,
      { tipo: 'texto', cor, x: textoPos.x, y: textoPos.y, texto: textoInput, size: 20 }];
    redraw(canvasRef.current, imgRef.current, shapesRef.current);
    setTextoInput('');
    setTextoPos(null);
  };

  const desfazer = () => {
    shapesRef.current = shapesRef.current.slice(0, -1);
    redraw(canvasRef.current, imgRef.current, shapesRef.current);
  };

  const handleEnviar = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(blob => { if (blob) onEnviar(blob); }, 'image/png');
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,
      display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12}}>
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',justifyContent:'center',
        background:'#1e1e1e',borderRadius:10,padding:'8px 14px'}}>
        {FERRAMENTAS.map(f => (
          <button key={f.id} title={f.title} onClick={() => setFerramenta(f.id)}
            style={{background:ferramenta===f.id?'var(--tema-cor)':'#333',color:'white',
              border:'none',borderRadius:6,padding:'6px 12px',cursor:'pointer',fontSize:16,fontWeight:600}}>
            {f.label}
          </button>
        ))}
        <div style={{width:1,height:28,background:'#444'}} />
        {CORES.map(c => (
          <button key={c.value} title={c.label} onClick={() => setCor(c.value)}
            style={{width:26,height:26,borderRadius:'50%',background:c.value,cursor:'pointer',
              border:cor===c.value?'3px solid white':'3px solid transparent'}} />
        ))}
        <div style={{width:1,height:28,background:'#444'}} />
        <button onClick={desfazer}
          style={{background:'#555',color:'white',border:'none',borderRadius:6,padding:'6px 12px',cursor:'pointer',fontSize:13}}>
          ↩ Desfazer
        </button>
      </div>
      <div style={{position:'relative'}}>
        <canvas ref={canvasRef}
          style={{display:'block',borderRadius:8,cursor:ferramenta==='texto'?'text':'crosshair',maxWidth:'85vw',maxHeight:'65vh'}}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
          onTouchStart={onMouseDown} onTouchMove={onMouseMove} onTouchEnd={onMouseUp}
        />
        {textoPos && ferramenta==='texto' && (
          <div style={{position:'absolute',top:0,left:0,pointerEvents:'none',width:'100%',height:'100%'}}>
            <div style={{position:'absolute',
              top:(textoPos.y*(canvasRef.current?.getBoundingClientRect().height/canvasRef.current?.height||1)),
              left:(textoPos.x*(canvasRef.current?.getBoundingClientRect().width/canvasRef.current?.width||1)),
              display:'flex',gap:4,pointerEvents:'all'}}>
              <input autoFocus value={textoInput} onChange={e=>setTextoInput(e.target.value)}
                onKeyDown={e=>{if(e.key==='Enter')adicionarTexto();if(e.key==='Escape'){setTextoPos(null);setTextoInput('');}}}
                style={{fontSize:16,padding:'2px 6px',borderRadius:4,border:`2px solid ${cor}`,
                  background:'rgba(0,0,0,0.7)',color:'white',width:160}}
                placeholder="Digite e Enter" />
              <button onClick={adicionarTexto}
                style={{background:cor,color:'white',border:'none',borderRadius:4,padding:'2px 8px',cursor:'pointer'}}>✓</button>
            </div>
          </div>
        )}
      </div>
      <div style={{display:'flex',gap:12}}>
        <button onClick={onCancelar}
          style={{background:'#e53935',color:'white',border:'none',borderRadius:8,padding:'10px 28px',cursor:'pointer',fontWeight:600,fontSize:14}}>
          ✕ Cancelar
        </button>
        <button onClick={handleEnviar}
          style={{background:'#25d366',color:'white',border:'none',borderRadius:8,padding:'10px 28px',cursor:'pointer',fontWeight:600,fontSize:14}}>
          ▶ Enviar imagem
        </button>
      </div>
    </div>
  );
}
